/**
 * Payment sheet (S6) — pick the tender(s) and complete the sale.
 * Cash (with change), Card / UPI (attested + reference), or Split (multi-tender).
 * @see prototype/pos/payment.html
 */
import { useState } from "react";
import { formatRupees } from "../invoices/lib/format-money.ts";

type Mode = "cash" | "card" | "upi";
export interface Tender {
  mode: Mode;
  amountPaise: string;
  reference?: string;
}

const toPaise = (s: string) => BigInt(Math.round((parseFloat(s.replace(/[^0-9.]/g, "")) || 0) * 100));
const METHODS: { id: Mode | "split"; label: string }[] = [
  { id: "cash", label: "Cash" },
  { id: "card", label: "Card" },
  { id: "upi", label: "UPI" },
  { id: "split", label: "Split" },
];

export function PaymentSheet({
  grandPaise,
  pending,
  onComplete,
  onCancel,
}: {
  grandPaise: string;
  pending: boolean;
  onComplete: (tenders: Tender[]) => void;
  onCancel: () => void;
}) {
  const grand = BigInt(grandPaise);
  const [method, setMethod] = useState<Mode | "split">("cash");
  const [cash, setCash] = useState("");
  const [ref, setRef] = useState("");
  const [split, setSplit] = useState<Tender[]>([]);
  const [splitMode, setSplitMode] = useState<Mode>("cash");
  const [splitAmt, setSplitAmt] = useState("");

  let tenders: Tender[] = [];
  let change = 0n;
  let ready = false;
  if (method === "cash") {
    const entered = cash === "" ? grand : toPaise(cash);
    tenders = [{ mode: "cash", amountPaise: entered.toString() }];
    change = entered > grand ? entered - grand : 0n;
    ready = entered >= grand;
  } else if (method === "card" || method === "upi") {
    tenders = [{ mode: method, amountPaise: grand.toString(), ...(ref.trim() ? { reference: ref.trim() } : {}) }];
    ready = true;
  } else {
    const sum = split.reduce((s, t) => s + BigInt(t.amountPaise), 0n);
    const hasCash = split.some((t) => t.mode === "cash");
    tenders = split;
    change = sum > grand && hasCash ? sum - grand : 0n;
    ready = sum >= grand;
  }
  const splitRemaining = grand - split.reduce((s, t) => s + BigInt(t.amountPaise), 0n);

  function addSplit() {
    const amt = splitAmt === "" ? (splitRemaining > 0n ? splitRemaining : 0n) : toPaise(splitAmt);
    if (amt <= 0n) return;
    setSplit((p) => [...p, { mode: splitMode, amountPaise: amt.toString() }]);
    setSplitAmt("");
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/50" onClick={onCancel} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-[min(460px,100vw)] flex-col border-l border-line bg-paper">
        <header className="flex items-center gap-3 border-b border-line px-5 py-4">
          <h2 className="flex-1 text-base font-bold">Take payment</h2>
          <button onClick={onCancel} className="grid h-9 w-9 place-items-center rounded-md border border-line text-ink-2 hover:bg-bg" aria-label="Close">✕</button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 pt-4">
          <div className="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-sky-700">Amount due</div>
            <div className="font-mono text-3xl font-bold tracking-tight">{formatRupees(grandPaise)}</div>
            {change > 0n && <div className="mt-0.5 text-xs text-muted">Change to return <b className="font-mono text-ok">{formatRupees(change.toString())}</b></div>}
          </div>

          <div className="mt-4 grid grid-cols-4 gap-2">
            {METHODS.map((m) => (
              <button
                key={m.id}
                onClick={() => setMethod(m.id)}
                className={`h-14 rounded-md border text-xs font-semibold ${method === m.id ? "border-ink bg-ink text-white" : "border-line bg-paper text-ink-2 hover:bg-bg"}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {method === "cash" && (
            <div className="mt-4">
              <div className="mb-1.5 text-xs font-semibold text-ink-2">Cash received</div>
              <div className="flex h-14 items-center gap-2 rounded-lg border-[1.5px] border-line-2 bg-paper px-4 focus-within:border-sky-600 focus-within:ring-4 focus-within:ring-sky-100">
                <span className="text-xl font-bold text-muted">₹</span>
                <input value={cash} onChange={(e) => setCash(e.target.value)} inputMode="decimal" placeholder={(Number(grand) / 100).toString()} autoFocus className="w-full border-0 bg-transparent font-mono text-2xl font-bold text-ink outline-none placeholder:text-muted" />
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[grand, ((grand / 10000n) + 1n) * 10000n, ((grand / 50000n) + 1n) * 50000n].map((v, i) => (
                  <button key={i} onClick={() => setCash((Number(v) / 100).toString())} className="h-11 rounded-md border border-line bg-raised font-mono text-sm font-bold text-ink-2 hover:bg-sky-50 hover:text-sky-700">
                    {formatRupees(v.toString())}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(method === "card" || method === "upi") && (
            <div className="mt-4">
              <div className="rounded-lg border border-line bg-raised p-3 text-sm text-ink-2">
                {method === "card" ? "Take the card on your own terminal." : "Show the UPI QR / take payment on your VPA."} Flint records the tender + reference — funds settle to your bank directly.
              </div>
              <div className="mt-3 mb-1.5 text-xs font-semibold text-ink-2">{method === "card" ? "Card reference (last 4 / auth)" : "UPI reference (UTR)"}</div>
              <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder={method === "card" ? "•••• 4421" : "UTR 4088…"} className="h-12 w-full rounded-lg border border-line-2 bg-paper px-3.5 font-mono text-base text-ink outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
            </div>
          )}

          {method === "split" && (
            <div className="mt-4">
              {split.map((t, i) => (
                <div key={i} className="mb-2 flex items-center gap-2 rounded-md border border-line bg-raised px-3 py-2 text-sm">
                  <span className="font-semibold uppercase">{t.mode}</span>
                  <span className="ml-auto font-mono font-bold">{formatRupees(t.amountPaise)}</span>
                  <button onClick={() => setSplit((p) => p.filter((_, j) => j !== i))} className="ml-1 text-muted hover:text-bad" aria-label="Remove">✕</button>
                </div>
              ))}
              <div className="mt-1 text-xs font-semibold text-muted">
                {splitRemaining > 0n ? <>Remaining <span className="font-mono text-bad">{formatRupees(splitRemaining.toString())}</span></> : "Fully tendered"}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {(["cash", "card", "upi"] as Mode[]).map((m) => (
                  <button key={m} onClick={() => setSplitMode(m)} className={`h-11 rounded-md border text-sm font-semibold ${m === splitMode ? "border-ink bg-ink text-white" : "border-line bg-paper text-ink-2"}`}>{m.toUpperCase()}</button>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <input value={splitAmt} onChange={(e) => setSplitAmt(e.target.value)} inputMode="decimal" placeholder={splitRemaining > 0n ? (Number(splitRemaining) / 100).toString() : "0"} className="h-11 flex-1 rounded-md border border-line-2 bg-paper px-3 font-mono text-sm outline-none focus:border-sky-600" />
                <button onClick={addSplit} className="h-11 rounded-md border border-sky-600 bg-sky-50 px-4 text-sm font-semibold text-sky-700">Add tender</button>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-line px-5 py-4">
          {change > 0n && (
            <div className="mb-3 flex items-center justify-between text-sm">
              <span>Change to return</span>
              <span className="font-mono text-lg font-bold text-ok">{formatRupees(change.toString())}</span>
            </div>
          )}
          <button
            disabled={!ready || pending}
            onClick={() => onComplete(tenders)}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-ok text-base font-bold text-white shadow-md hover:brightness-95 disabled:opacity-50"
          >
            {pending ? "Completing…" : "Complete sale"}
          </button>
        </div>
      </aside>
    </>
  );
}
